import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fmtRange } from "./statsFormat";

/**
 * Change against the comparison period. Direction is conveyed by arrow + sign + color, never by
 * color alone, and the meaning ("mejora" / "empeora") is spelled out for screen readers.
 * @param {{delta: {text, tone, arrow, word}|null}} props  Result of `computeDelta`.
 */
export function Delta({ delta }) {
  if (!delta) return null;
  return (
    <span className={`stats-delta ${delta.tone}`} title={delta.word}>
      <span aria-hidden="true">{delta.arrow}</span>
      {delta.text}
      <span className="stats-sr-only"> ({delta.word})</span>
    </span>
  );
}

const SPARK_W = 240;
const SPARK_H = 34;
const SPARK_PAD = 3;

/** Decorative trend line under a KPI (the exact numbers live in the Evolución tab). */
export function Sparkline({ values }) {
  const viewBox = `0 0 ${SPARK_W} ${SPARK_H}`;
  if (values.length < 2) return <svg className="stats-kpi__spark" aria-hidden="true" viewBox={viewBox} />;
  const min = Math.min(...values);
  const span = Math.max(...values) - min || 1;
  const x = (i) => SPARK_PAD + (i * (SPARK_W - 2 * SPARK_PAD)) / (values.length - 1);
  const y = (v) => SPARK_H - SPARK_PAD - ((v - min) / span) * (SPARK_H - 2 * SPARK_PAD);
  const line = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  return (
    <svg className="stats-kpi__spark" aria-hidden="true" viewBox={viewBox} preserveAspectRatio="none">
      <path d={`${line}L${x(values.length - 1)},${SPARK_H}L${x(0)},${SPARK_H}Z`} fill="rgba(177,194,14,0.14)" />
      <path
        d={line}
        fill="none"
        stroke="var(--stats-accent)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/**
 * Round "i" button that opens a small popover with how a metric is calculated.
 * Closes with Escape (returning focus to the button), a click elsewhere, scroll or resize, so a
 * floating box never ends up stranded away from its anchor.
 *
 * @param {{definition: [string, string], label: string}} props  `definition` is `[title, text]`, one
 *   entry of a screen's definitions table (courtStatsDefinitions, beautyStatsDefinitions).
 */
export function InfoButton({ definition, label }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 16, top: 0 });
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const [title, text] = definition;

  // Place the popover under the button, flipping above it when there is no room below.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !popoverRef.current) return;
    const anchor = buttonRef.current.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = popoverRef.current;
    const left = Math.max(16, Math.min(anchor.left, window.innerWidth - width - 16));
    let top = anchor.bottom + 8;
    if (top + height > window.innerHeight - 8) top = Math.max(8, anchor.top - height - 8);
    setPosition({ left, top });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (!popoverRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="stats-info-btn"
        aria-label={`Cómo se calcula ${label}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        i
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={title}
          className="stats-popover"
          style={{ left: position.left, top: position.top }}
        >
          <h3>{title}</h3>
          <p>{text}</p>
        </div>
      )}
    </>
  );
}

/**
 * "13 sept – 12 oct 2026 · comparado con 14 ago – 12 sept 2026 · Datos hasta ayer": the ranges the
 * numbers below answer, so a stale label can never describe other numbers.
 * @param {{shown: {from: string, to: string}, comparison: {from: string, to: string}|null}} props
 */
export function StatsRangeCaption({ shown, comparison }) {
  return (
    <p className="stats-range-caption">
      <strong>{fmtRange(shown)}</strong>
      {comparison && <> · comparado con {fmtRange(comparison)}</>}
      {" "}· Datos hasta ayer
    </p>
  );
}
